import { useEffect, useState } from "react";
import { usersApi, businessesApi, authApi } from "@/lib/api";
import { useAuth, isMaster, ROLE_LABELS, userBusinessIds } from "@/lib/auth";
import { toast } from "sonner";
import { UserPlus, Ban, Check, Edit2, X, Save, Loader2, Trash2 } from "lucide-react";

const ALL_ROLES = ["branch_user", "accounting_user", "business_admin", "master_admin"];

function MultiSelect({ options, value = [], onChange, testid }) {
  const toggle = (id) => {
    if (value.includes(id)) onChange(value.filter((v) => v !== id));
    else onChange([...value, id]);
  };
  return (
    <div className="flex flex-wrap gap-2" data-testid={testid}>
      {options.map((o) => {
        const active = value.includes(o.id);
        return (
          <button
            key={o.id}
            type="button"
            onClick={() => toggle(o.id)}
            className={`px-3 py-1.5 rounded-full text-xs font-medium border transition-colors ${
              active
                ? "bg-espresso text-canvas border-espresso"
                : "bg-white text-espresso-ink border-canvas-border hover:bg-canvas-border/40"
            }`}
          >
            {o.name}
          </button>
        );
      })}
      {options.length === 0 && <span className="text-xs text-espresso-ink/40">Nothing to pick</span>}
    </div>
  );
}

export default function Users() {
  const { user } = useAuth();
  const [businesses, setBusinesses] = useState([]);
  const [allBranches, setAllBranches] = useState([]);
  const [users, setUsers] = useState([]);
  const [inviting, setInviting] = useState(false);
  const [editing, setEditing] = useState(null); // user_id being edited
  const [editForm, setEditForm] = useState({ business_ids: [], branch_ids: [], role: "", full_name: "", email: "" });
  const [form, setForm] = useState({ email: "", role: "branch_user", full_name: "", business_ids: [], branch_ids: [] });

  const load = async () => {
    const b = await businessesApi.list();
    setBusinesses(b);
    const branchLists = await Promise.all(b.map((biz) => businessesApi.branches(biz.id).catch(() => [])));
    setAllBranches(branchLists.flat());
    const u = await usersApi.list();
    setUsers(u);
  };

  useEffect(() => { load(); }, []);

  const branchesForBusinesses = (bizIds) => allBranches.filter((br) => bizIds.includes(br.business_id));

  const invite = async (e) => {
    e.preventDefault();
    if (form.business_ids.length === 0 && form.role !== "master_admin") {
      return toast.error("Assign at least one business");
    }
    setInviting(true);
    try {
      const res = await authApi.invite(form);
      toast.success(res.email_sent ? "Invite email sent" : "Invite created (email failed)");
      setForm({ email: "", role: "branch_user", full_name: "", business_ids: [], branch_ids: [] });
      load();
    } catch (e) { toast.error(e?.response?.data?.detail || "Invite failed"); }
    finally { setInviting(false); }
  };

  const startEdit = (u) => {
    setEditing(u.id);
    setEditForm({
      business_ids: u.business_ids || (u.business_id ? [u.business_id] : []),
      branch_ids: u.branch_ids || (u.branch_id ? [u.branch_id] : []),
      role: u.role,
      full_name: u.full_name || "",
      email: u.email || "",
    });
  };

  const saveEdit = async () => {
    try {
      if (editForm.full_name !== undefined || editForm.email !== undefined) {
        await usersApi.update(editing, { full_name: editForm.full_name, email: editForm.email });
      }
      await usersApi.updateLinks(editing, { business_ids: editForm.business_ids, branch_ids: editForm.branch_ids, role: editForm.role });
      toast.success("User updated");
      setEditing(null);
      load();
    } catch (e) { toast.error(e?.response?.data?.detail || "Update failed"); }
  };

  const toggleActive = async (u) => {
    if (u.active) await usersApi.deactivate(u.id);
    else await usersApi.activate(u.id);
    load();
  };

  const availableRoles = isMaster(user) ? ALL_ROLES : ALL_ROLES.filter((r) => r !== "master_admin");
  const availableBusinesses = isMaster(user) ? businesses : businesses.filter((b) => userBusinessIds(user).includes(b.id));

  return (
    <div className="space-y-8">
      <div>
        <h1 className="font-display text-4xl font-bold text-espresso" data-testid="users-title">Team</h1>
        <p className="text-espresso-ink/60 mt-2">Invite users and link them to one or more businesses and branches. Only <b>Accounting Users</b> can publish documents to Xero / Zoho.</p>
      </div>

      <form onSubmit={invite} className="bg-white border border-canvas-border rounded-2xl p-6 shadow-soft space-y-5" data-testid="invite-form">
        <h2 className="font-display text-lg font-bold text-espresso">Invite user</h2>
        <div className="grid grid-cols-2 gap-4">
          <div>
            <label className="text-xs uppercase tracking-widest text-espresso-ink/60 font-semibold">Email</label>
            <input data-testid="invite-email" required type="email" placeholder="email@company.com" value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} className="w-full mt-1.5 px-3 py-2.5 border border-canvas-border rounded-lg brand-focus" />
          </div>
          <div>
            <label className="text-xs uppercase tracking-widest text-espresso-ink/60 font-semibold">Full name</label>
            <input data-testid="invite-name" placeholder="Optional" value={form.full_name} onChange={(e) => setForm({ ...form, full_name: e.target.value })} className="w-full mt-1.5 px-3 py-2.5 border border-canvas-border rounded-lg brand-focus" />
          </div>
        </div>
        <div>
          <label className="text-xs uppercase tracking-widest text-espresso-ink/60 font-semibold">Role</label>
          <div className="mt-1.5 flex flex-wrap gap-2" data-testid="invite-role">
            {availableRoles.map((r) => (
              <button key={r} type="button" onClick={() => setForm({ ...form, role: r })} className={`px-3 py-1.5 rounded-full text-xs font-medium border transition-colors ${form.role === r ? "bg-espresso text-canvas border-espresso" : "bg-white text-espresso-ink border-canvas-border hover:bg-canvas-border/40"}`}>{ROLE_LABELS[r]}</button>
            ))}
          </div>
        </div>
        <div>
          <label className="text-xs uppercase tracking-widest text-espresso-ink/60 font-semibold">Assigned businesses</label>
          <div className="mt-1.5"><MultiSelect testid="invite-businesses" options={availableBusinesses} value={form.business_ids} onChange={(v) => setForm({ ...form, business_ids: v, branch_ids: form.branch_ids.filter((bid) => branchesForBusinesses(v).some((br) => br.id === bid)) })} /></div>
        </div>
        <div>
          <label className="text-xs uppercase tracking-widest text-espresso-ink/60 font-semibold">Assigned branches <span className="text-espresso-ink/40 normal-case">(from selected businesses)</span></label>
          <div className="mt-1.5"><MultiSelect testid="invite-branches" options={branchesForBusinesses(form.business_ids)} value={form.branch_ids} onChange={(v) => setForm({ ...form, branch_ids: v })} /></div>
        </div>
        <div className="flex justify-end">
          <button data-testid="invite-submit" disabled={inviting} className="inline-flex items-center gap-2 px-5 py-2.5 bg-espresso text-canvas rounded-lg text-sm font-semibold hover:bg-espresso-light disabled:opacity-60">
            {inviting ? <Loader2 className="w-4 h-4 animate-spin" /> : <UserPlus className="w-4 h-4" />} Send invite
          </button>
        </div>
      </form>

      <div className="bg-white border border-canvas-border rounded-2xl overflow-x-auto shadow-soft" data-testid="users-table-wrap">
        {users.length === 0 ? (
          <div className="p-14 text-center text-espresso-ink/50">No team members yet.</div>
        ) : (
          <table className="w-full min-w-[1100px]">
            <thead className="bg-canvas text-xs uppercase tracking-wider text-espresso-ink/60">
              <tr>
                <th className="text-left px-6 py-3">Name</th>
                <th className="text-left px-6 py-3">Email</th>
                <th className="text-left px-6 py-3">Role</th>
                <th className="text-left px-6 py-3">Businesses</th>
                <th className="text-left px-6 py-3">Branches</th>
                <th className="text-left px-6 py-3">Status</th>
                <th className="text-right px-6 py-3 w-56 bg-canvas sticky right-0 z-10">Actions</th>
              </tr>
            </thead>
            <tbody>
              {users.map((u) => {
                const uBiz = u.business_ids || (u.business_id ? [u.business_id] : []);
                const uBranches = u.branch_ids || (u.branch_id ? [u.branch_id] : []);
                const isEditing = editing === u.id;
                return (
                  <tr key={u.id} className="border-t border-canvas-border align-top" data-testid={`user-row-${u.id}`}>
                    <td className="px-6 py-3 font-medium text-espresso">
                      {isEditing ? (
                        <input value={editForm.full_name} onChange={(e) => setEditForm({ ...editForm, full_name: e.target.value })} className="px-2 py-1 border border-canvas-border text-xs brand-focus w-40" data-testid={`edit-name-${u.id}`} placeholder="Full name" />
                      ) : (u.full_name || "—")}
                    </td>
                    <td className="px-6 py-3 text-sm text-espresso-ink">
                      {isEditing ? (
                        <input value={editForm.email} onChange={(e) => setEditForm({ ...editForm, email: e.target.value })} type="email" className="px-2 py-1 border border-canvas-border text-xs brand-focus w-56" data-testid={`edit-email-${u.id}`} />
                      ) : u.email}
                    </td>
                    <td className="px-6 py-3">
                      {isEditing ? (
                        <select value={editForm.role} onChange={(e) => setEditForm({ ...editForm, role: e.target.value })} className="px-2 py-1 border border-canvas-border rounded text-xs brand-focus">
                          {availableRoles.map((r) => <option key={r} value={r}>{ROLE_LABELS[r]}</option>)}
                        </select>
                      ) : (
                        <span className="text-xs uppercase tracking-widest text-espresso-ink/70 font-semibold">{ROLE_LABELS[u.role] || u.role}</span>
                      )}
                    </td>
                    <td className="px-6 py-3">
                      {isEditing ? (
                        <MultiSelect options={availableBusinesses} value={editForm.business_ids} onChange={(v) => setEditForm({ ...editForm, business_ids: v, branch_ids: editForm.branch_ids.filter((bid) => branchesForBusinesses(v).some((br) => br.id === bid)) })} />
                      ) : (
                        <div className="flex flex-wrap gap-1">
                          {uBiz.length === 0 ? <span className="text-xs text-espresso-ink/40">All (master)</span> : uBiz.map((bid) => {
                            const b = businesses.find((x) => x.id === bid);
                            return b ? <span key={bid} className="text-[10px] uppercase tracking-widest bg-gold-soft text-gold px-2 py-0.5 rounded font-semibold">{b.code}</span> : null;
                          })}
                        </div>
                      )}
                    </td>
                    <td className="px-6 py-3">
                      {isEditing ? (
                        <MultiSelect options={branchesForBusinesses(editForm.business_ids)} value={editForm.branch_ids} onChange={(v) => setEditForm({ ...editForm, branch_ids: v })} />
                      ) : (
                        <div className="flex flex-wrap gap-1">
                          {uBranches.length === 0 ? <span className="text-xs text-espresso-ink/40">—</span> : uBranches.map((bid) => {
                            const br = allBranches.find((x) => x.id === bid);
                            return br ? <span key={bid} className="text-[10px] uppercase tracking-widest bg-canvas-border text-espresso px-2 py-0.5 rounded font-semibold">{br.code}</span> : null;
                          })}
                        </div>
                      )}
                    </td>
                    <td className="px-6 py-3">
                      {u.active ? (
                        <span className="inline-flex items-center gap-1 text-status-synced text-xs font-semibold"><Check className="w-3 h-3" /> Active</span>
                      ) : (
                        <span className="inline-flex items-center gap-1 text-espresso-ink/50 text-xs font-semibold"><Ban className="w-3 h-3" /> Inactive</span>
                      )}
                    </td>
                    <td className="px-6 py-3 text-right bg-white sticky right-0 z-10 border-l border-canvas-border">
                      {isEditing ? (
                        <div className="flex gap-2 justify-end">
                          <button onClick={() => setEditing(null)} data-testid={`cancel-edit-${u.id}`} className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-xs font-semibold text-espresso-ink border border-canvas-border hover:bg-canvas-border/40"><X className="w-3.5 h-3.5" /> Cancel</button>
                          <button onClick={saveEdit} data-testid={`save-user-${u.id}`} className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-xs font-semibold bg-espresso text-canvas hover:bg-espresso-light"><Save className="w-3.5 h-3.5" /> Save</button>
                        </div>
                      ) : (
                        <div className="flex gap-2 justify-end items-center flex-wrap">
                          <button
                            onClick={() => startEdit(u)}
                            data-testid={`edit-user-${u.id}`}
                            className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-xs font-semibold text-espresso border border-canvas-border hover:bg-canvas-border/40"
                          >
                            <Edit2 className="w-3.5 h-3.5" /> Edit
                          </button>
                          <button
                            data-testid={`toggle-user-${u.id}`}
                            onClick={() => toggleActive(u)}
                            className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-xs font-semibold text-espresso-ink border border-canvas-border hover:bg-canvas-border/40"
                          >
                            {u.active ? <><Ban className="w-3.5 h-3.5" /> Deactivate</> : <><Check className="w-3.5 h-3.5" /> Activate</>}
                          </button>
                          <button
                            data-testid={`delete-user-${u.id}`}
                            onClick={async () => {
                              if (u.id === user?.id) { toast.error("You cannot delete your own account"); return; }
                              if (!window.confirm(`Delete ${u.email}? This cannot be undone.`)) return;
                              try {
                                await usersApi.del(u.id);
                                toast.success(`Deleted ${u.email}`);
                                load();
                              } catch (e) {
                                const msg = e?.response?.data?.detail || e?.message || "Delete failed";
                                toast.error(String(msg));
                              }
                            }}
                            className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-xs font-semibold text-status-duplicate border border-status-duplicate/40 hover:bg-status-duplicate/10"
                          >
                            <Trash2 className="w-3.5 h-3.5" /> Delete
                          </button>
                        </div>
                      )}
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        )}
      </div>
    </div>
  );
}
